import {
  Box,
  Header,
  SpaceBetween,
  Cards,
  Link,
  ColumnLayout,
  Pagination,
  CollectionPreferences,
  CollectionPreferencesProps,
  Table,
  Button,
  Modal,
} from '@cloudscape-design/components';

import { DashboardViewLayout } from '../../components/DashboardViewLayout';
import { useNavigate } from '@tanstack/react-router';
import {
  useUrlFilterState,
  useUrlRangeState,
  useUrlSortingState,
} from '../../common/filtering/filters';
import { useState } from 'react';
import { PlaceTable } from '../../components/dashboard/PlaceTable';
import { Place } from '../../common/types';
import { AddToListModal } from '../../components/AddToListModal';
import { SearchPlacesModal } from '../../components/SearchPlacesModal';
import { hidePlaces, unhidePlaces } from '../../common/api/places';
import { UpdatePlacesModal } from '../../components/UpdatePlacesModal';

export default function View() {
  const navigate = useNavigate();
  const resourceName = 'Places';

  const { query, setQuery } = useUrlFilterState();
  // relative times are in the past vs future
  const past = true;
  const { range, setRange } = useUrlRangeState(past);
  const { sortingField, setSortingField, sortDirection, setSortDirection } = useUrlSortingState(
    'updated_at',
    'desc',
  );
  const [now, setNow] = useState(new Date());
  const [selectedItems, setSelectedItems] = useState<Place[]>([]);
  const [addToListOpen, setAddToListOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [textQuery, setTextQuery] = useState('');

  const showingHidden = query.tokens.filter((token) => token.propertyKey === 'hidden').length !== 0;

  return (
    <DashboardViewLayout breadcrumbs={[{ text: resourceName, href: '' }]} contentType="table">
      <PlaceTable
        now={now}
        setNow={setNow}
        query={query}
        setQuery={setQuery}
        range={range}
        setRange={setRange}
        sortingField={sortingField}
        setSortingField={setSortingField}
        sortDirection={sortDirection}
        setSortDirection={setSortDirection}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        textQuery={textQuery}
        setTextQuery={setTextQuery}
        additionalActions={
          <SpaceBetween size="xs" direction="horizontal">
            <Button
              variant="normal"
              iconName="add-plus"
              onClick={() => setAddToListOpen(true)}
              disabled={selectedItems.length === 0}
            >
              Add to List
            </Button>
            {showingHidden ? (
              <Button
                variant="normal"
                iconName="undo"
                onClick={async () => {
                  await unhidePlaces(selectedItems.map((item) => item.uuid));
                  setSelectedItems([]);
                  setQuery({
                    operation: query.operation,
                    tokens: query.tokens.filter((token) => token.propertyKey !== 'hidden'),
                  });
                  setNow(new Date(new Date().getTime() + 1000));
                }}
                disabled={selectedItems.length === 0}
              >
                Unhide
              </Button>
            ) : (
              <Button
                variant="normal"
                iconName="remove"
                onClick={async () => {
                  await hidePlaces(selectedItems.map((item) => item.uuid));
                  setSelectedItems([]);
                  setNow(new Date(new Date().getTime() + 1000));
                }}
                disabled={selectedItems.length === 0}
              >
                Hide
              </Button>
            )}
            {selectedItems.length > 0 ? (
              <Button
                variant="primary"
                iconName="gen-ai"
                onClick={() => {
                  setUpdateOpen(true);
                }}
              >
                Update Places
              </Button>
            ) : (
              <Button
                variant="primary"
                iconName="gen-ai"
                onClick={() => {
                  setCreateOpen(true);
                }}
              >
                Add new Places
              </Button>
            )}
          </SpaceBetween>
        }
      />
      <AddToListModal
        places={selectedItems}
        open={addToListOpen}
        setOpen={setAddToListOpen}
        onComplete={() => {
          setSelectedItems?.([]);
          setNow(new Date(new Date().getTime() + 1000));
        }}
      />
      <SearchPlacesModal
        open={createOpen}
        setOpen={setCreateOpen}
        onComplete={() => {
          setNow(new Date(new Date().getTime() + 1000));
        }}
      />
      <UpdatePlacesModal
        open={updateOpen}
        setOpen={setUpdateOpen}
        places={selectedItems}
        onComplete={() => {
          setSelectedItems([]);
          setNow(new Date(new Date().getTime() + 1000));
        }}
      />
    </DashboardViewLayout>
  );
}
